/**
 * OpportunityScore.jsx
 *
 * Displays AXO opportunity scores for a single company.
 * Intended to be embedded in the company detail panel / drawer.
 *
 * Usage:
 *   <OpportunityScore companyId={company.id} compact={false} isInternal={isInternalUser(user?.email)} />
 *
 * Props:
 *   companyId    string   - Required. The company DB id.
 *   compact      boolean  - If true, shows a condensed pill-only view (for table rows).
 *   className    string   - Optional extra class.
 *   isInternal   boolean  - If true (Peach staff emails only), uses the live API with full
 *                           internal product scores (6ELA, AXO, Telco, Wizzyl).
 *                           If false (default), uses the demo API, safe for external users.
 *                           Use the exported `isInternalUser(email)` helper to derive this.
 */

import React, { useState, useCallback, useEffect } from "react";
import apiService from "../services/api";
import demoApiService from "../services/demoApi";

// ─────────────────────────────────────────────────────────────────────────────
// DOMAIN GUARD
// Returns true for Peach internal users who should see the full internal score
// (with real product names like 6ELA, AXO, Telco, Wizzyl).
// ─────────────────────────────────────────────────────────────────────────────

const INTERNAL_DOMAINS = ["peachloves.co.uk", "peachlovesdigital.co.uk"];

export function isInternalUser(email) {
  if (!email) return false;
  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  return INTERNAL_DOMAINS.includes(domain);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT METADATA
// ─────────────────────────────────────────────────────────────────────────────

const PRODUCTS = [
  {
    key: "wizzyl",
    label: "Wizzyl",
    sub: "Website",
    color: "var(--accent-light)",
    bg: "var(--accent-dim)",
    border: "var(--accent-glow)",
    icon: (
      <svg
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
        />
      </svg>
    ),
  },
  {
    key: "telco",
    label: "Telco",
    sub: "Comms",
    color: "var(--green)",
    bg: "var(--green-dim)",
    border: "var(--green-border)",
    icon: (
      <svg
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
      </svg>
    ),
  },
  {
    key: "sixEla",
    label: "6ELA",
    sub: "Debt recovery",
    color: "var(--amber)",
    bg: "var(--amber-dim)",
    border: "var(--amber-border)",
    icon: (
      <svg
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    key: "axo_signal",
    label: "AXO",
    sub: "Growth",
    color: "var(--purple)",
    bg: "var(--purple-dim)",
    border: "rgba(139,92,246,0.25)",
    icon: (
      <svg
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
        />
      </svg>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function ScoreBar({ score, color }) {
  return (
    <div
      style={{
        width: "100%",
        height: 5,
        background: "var(--border)",
        borderRadius: 9999,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${score}%`,
          height: "100%",
          background: color,
          borderRadius: 9999,
          transition: "width 0.5s cubic-bezier(0.16,1,0.3,1)",
        }}
      />
    </div>
  );
}

function PriorityBadge({ label, tier }) {
  const colors = {
    primary: {
      bg: "rgba(8,42,88,0.1)",
      border: "rgba(8,42,88,0.25)",
      color: "var(--accent)",
    },
    secondary: {
      bg: "rgba(0,0,0,0.04)",
      border: "rgba(0,0,0,0.14)",
      color: "var(--text-muted)",
    },
  };
  const c = colors[tier];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 7px",
        borderRadius: 9999,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.color,
        fontFamily: "var(--mono)",
      }}
    >
      {tier === "primary" ? "★ " : ""}
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPACT VIEW (pill row for table)
// ─────────────────────────────────────────────────────────────────────────────

function CompactScore({ data }) {
  if (!data) return null;
  const { primary, secondary, primaryLabel, secondaryLabel } = data;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
    >
      {primary && <PriorityBadge label={primaryLabel} tier="primary" />}
      {secondary && <PriorityBadge label={secondaryLabel} tier="secondary" />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FULL SCORE PANEL
// ─────────────────────────────────────────────────────────────────────────────

function FullScorePanel({
  data,
  serviceProfiles = [],
  onRefreshFinancials = null,
  refreshingFinancials = false,
  refreshFinancialsError = null,
}) {
  if (!data) return null;

  const {
    scores,
    primary,
    secondary,
    primaryLabel,
    secondaryLabel,
    recommendedAction,
    meta,
    insitesScore,
    insitesStatus,
    details,
    filedAccountsUrl,
    // AXO's own snapshot-derived website health (Insites retired). Falls
    // back to the legacy insitesScore for older cached scores.
    websiteHealth,
  } = data;

  // Map the customer's own services onto the scorer's product keys. The
  // scorer uses "sixEla" internally; the stored taxonomy uses "6ela".
  const servicesByProduct = {};
  for (const svc of Array.isArray(serviceProfiles) ? serviceProfiles : []) {
    const key = svc?.productKey === "6ela" ? "sixEla" : svc?.productKey;
    if (!key) continue;
    if (!servicesByProduct[key]) servicesByProduct[key] = [];
    servicesByProduct[key].push(svc);
  }
  // A priced service is the one that drives Phase 5 value, so show it first
  // on the card when a product has more than one service.
  for (const list of Object.values(servicesByProduct)) {
    list.sort((a, b) => {
      const ap = Number(a.priceGbp) > 0 ? 0 : 1;
      const bp = Number(b.priceGbp) > 0 ? 0 : 1;
      return ap - bp;
    });
  }
  const serviceFor = (key) =>
    (servicesByProduct[key] ||
      (key === "6ela" ? servicesByProduct.sixEla : null))?.[0] || null;
  const primaryService = primary ? serviceFor(primary) : null;
  const secondaryService = secondary ? serviceFor(secondary) : null;
  const unmappedServices = (Array.isArray(serviceProfiles)
    ? serviceProfiles
    : []
  ).filter((svc) => !svc.productKey);
  const healthScore = websiteHealth ?? insitesScore;
  // Balance-sheet signal from the filed accounts (see
  // financialHealthScoring.js). Present on the response's `details`.
  // Default to "unavailable" so the panel (and its refresh action) still shows
  // for older cached scores that predate the financial-health field.
  const financialHealth = details?.financialHealth || { available: false };
  const fhFigures = financialHealth?.figures || {};
  const fhRatio =
    fhFigures.currentAssets != null &&
    fhFigures.creditorsDueWithinOneYear != null &&
    Math.abs(fhFigures.creditorsDueWithinOneYear) > 0
      ? fhFigures.currentAssets / Math.abs(fhFigures.creditorsDueWithinOneYear)
      : null;
  const fmtMoney = (n) =>
    n == null ? null : `£${Math.round(n).toLocaleString()}`;
  const fhRows = financialHealth?.available
    ? [
        fmtMoney(fhFigures.netAssets) && {
          label: "Net assets",
          value: fmtMoney(fhFigures.netAssets),
        },
        fhRatio != null && {
          label: "Current ratio",
          value: fhRatio.toFixed(2),
        },
        fmtMoney(fhFigures.netCurrentAssets) && {
          label: "Net current assets",
          value: fmtMoney(fhFigures.netCurrentAssets),
        },
        fmtMoney(fhFigures.debtors) && {
          label: "Debtors",
          value: fmtMoney(fhFigures.debtors),
        },
        fmtMoney(fhFigures.cash) && {
          label: "Cash",
          value: fmtMoney(fhFigures.cash),
        },
      ].filter(Boolean)
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Priority summary */}
      <div
        style={{
          padding: "12px 14px",
          background: "rgba(8,42,88,0.04)",
          border: "1px solid rgba(8,42,88,0.12)",
          borderRadius: 8,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "var(--text-faint)",
            fontFamily: "var(--mono)",
          }}
        >
          Opportunity Classification
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {primary && (
            <PriorityBadge
              label={primaryService?.name || primaryLabel}
              tier="primary"
            />
          )}
          {secondary && (
            <PriorityBadge
              label={secondaryService?.name || secondaryLabel}
              tier="secondary"
            />
          )}
          {!primary && (
            <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
              No clear opportunity identified
            </span>
          )}
        </div>
        {recommendedAction && (
          <div
            style={{
              fontSize: 12,
              color: "var(--text)",
              lineHeight: 1.5,
              paddingTop: 2,
            }}
          >
            <span
              style={{
                color: "var(--text-faint)",
                fontFamily: "var(--mono)",
                fontSize: 10,
              }}
            >
              Action →{" "}
            </span>
            {recommendedAction}
          </div>
        )}
      </div>

      {/* Score bars */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {PRODUCTS.map((product) => {
          const score = scores[product.key] ?? 0;
          const isPrimary =
            primary === product.key ||
            (primary === "6ela" && product.key === "sixEla");
          const isSecondary =
            secondary === product.key ||
            (secondary === "6ela" && product.key === "sixEla");
          const serviceList = servicesByProduct[product.key] || [];
          const service = serviceList[0] || null;
          const additionalServices = serviceList.slice(1);

          return (
            <div
              key={product.key}
              style={{
                padding: "10px 12px",
                background: isPrimary ? product.bg : "transparent",
                border: `1px solid ${isPrimary ? product.border : "var(--border)"}`,
                borderRadius: 8,
                transition: "background 0.2s",
              }}
            >
              {/* ── Header row ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: product.color }}>{product.icon}</span>
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text)",
                        lineHeight: 1,
                      }}
                    >
                      {service?.name || product.label}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--text-faint)",
                        fontFamily: "var(--mono)",
                        letterSpacing: "0.04em",
                        marginTop: 1,
                      }}
                    >
                      {service
                        ? `${product.label} · ${product.sub}`
                        : product.sub}
                    </div>
                    {service && (
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--text-muted)",
                          marginTop: 2,
                        }}
                      >
                        {Number(service.priceGbp) > 0
                          ? `Deal value: £${Number(
                              service.priceGbp,
                            ).toLocaleString("en-GB")}`
                          : "No deal value set"}
                      </div>
                    )}
                    {additionalServices.length > 0 && (
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--text-faint)",
                          marginTop: 1,
                        }}
                      >
                        Also:{" "}
                        {additionalServices
                          .map((svc) => svc.name)
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                  {isPrimary && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        padding: "1px 6px",
                        borderRadius: 9999,
                        background: product.color,
                        color: "#fff",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      Primary
                    </span>
                  )}
                  {isSecondary && !isPrimary && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        padding: "1px 6px",
                        borderRadius: 9999,
                        background: "var(--border)",
                        color: "var(--text-muted)",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      Secondary
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    letterSpacing: "-0.03em",
                    color:
                      score >= 60
                        ? product.color
                        : score >= 40
                          ? "var(--amber)"
                          : "var(--text-faint)",
                    fontFamily: "var(--sans)",
                  }}
                >
                  {score}
                </div>
              </div>

              {/* ── Progress bar ── */}
              <ScoreBar score={score} color={product.color} />

              {/* ── Wizzyl‑specific sub‑score ── */}
              {product.key === "wizzyl" && (
                <div style={{ marginTop: 8 }}>
                  <div
                    style={{ display: "flex", gap: 6, alignItems: "baseline" }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        color: "var(--text-faint)",
                        fontFamily: "var(--mono)",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Website health →
                    </span>
                    {healthScore != null ? (
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {healthScore}
                      </span>
                    ) : insitesStatus === "pending" ? (
                      <span
                        style={{
                          color: "var(--green)",
                          fontSize: 12,
                          animation: "pulse 2s infinite",
                          fontFamily: "var(--mono)",
                        }}
                      >
                        Snapshotting website…
                      </span>
                    ) : insitesStatus === "error" ||
                      insitesStatus === "failed" ? (
                      <span
                        style={{
                          color: "var(--red)",
                          fontSize: 12,
                          fontFamily: "var(--mono)",
                        }}
                      >
                        Audit unavailable
                      </span>
                    ) : (
                      <span
                        style={{ color: "var(--text-faint)", fontSize: 12 }}
                      >
                        N/A
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {unmappedServices.length > 0 && (
        <div
          style={{
            padding: "12px 14px",
            border: "1px solid var(--border)",
            borderRadius: 8,
            background: "var(--surface-raised)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "var(--text-faint)",
              fontFamily: "var(--mono)",
            }}
          >
            Services not mapped to a product
          </div>
          {unmappedServices.map((svc, i) => (
            <div
              key={`${svc.name || "service"}-${i}`}
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 12,
                fontSize: 12,
                color: "var(--text)",
              }}
            >
              <span>{svc.name || "Unnamed service"}</span>
              <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                {Number(svc.priceGbp) > 0
                  ? `£${Number(svc.priceGbp).toLocaleString("en-GB")}`
                  : "No value set"}
              </span>
            </div>
          ))}
          <div style={{ fontSize: 10, color: "var(--text-faint)" }}>
            Map them to an AXO product in Account settings to show them on the
            cards above and use their value in Phase 5.
          </div>
        </div>
      )}

      {/* Financial health, balance sheet parsed from the filed accounts
          (Companies House iXBRL). Shows the figures the scorer actually
          used, or a clear "none on file" line, never a placeholder number. */}
      {financialHealth && (
        <div
          style={{
            padding: "18px 20px",
            background: "var(--surface-raised)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Header: title left, period right, shared baseline */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              paddingBottom: 14,
              borderBottom: "1px solid var(--border)",
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "var(--text-muted)",
                fontFamily: "var(--mono)",
                lineHeight: 1.2,
              }}
            >
              Financial health
            </span>
            {financialHealth.available && fhFigures.periodEnd && (
              <span
                style={{
                  fontSize: 10,
                  color: "var(--text-faint)",
                  fontFamily: "var(--mono)",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                Year to {fhFigures.periodEnd}
              </span>
            )}
          </div>

          {financialHealth.available ? (
            <>
              {fhRows.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {fhRows.map((item, i) => (
                    <div
                      key={item.label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 16,
                        padding: "12px 0",
                        borderTop: i === 0 ? "none" : "1px solid var(--border)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--text-muted)",
                          fontFamily: "var(--mono)",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          lineHeight: 1.3,
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          lineHeight: 1.2,
                          color: "var(--text)",
                          fontVariantNumeric: "tabular-nums",
                          textAlign: "right",
                        }}
                      >
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {financialHealth.signals?.length > 0 && (
                <div
                  style={{
                    fontSize: 11,
                    lineHeight: 1.4,
                    color: "var(--text-muted)",
                    fontFamily: "var(--mono)",
                  }}
                >
                  {financialHealth.signals.join(" · ")}
                </div>
              )}
              <div
                style={{
                  fontSize: 10,
                  lineHeight: 1.4,
                  color: "var(--text-faint)",
                }}
              >
                From the filed accounts (Companies House), nudges product fit
                only.
              </div>
            </>
          ) : (
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                color: "var(--text-faint)",
              }}
            >
              No machine-readable figures on file, open the filed accounts to
              read the balance sheet. Liquidity isn't part of this score.
            </div>
          )}

          {/* Actions: equal height, vertically centred, link pushed right */}
          {(onRefreshFinancials || filedAccountsUrl) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 12,
                paddingTop: 16,
                borderTop: "1px solid var(--border)",
              }}
            >
              {onRefreshFinancials && (
                <button
                  type="button"
                  onClick={onRefreshFinancials}
                  disabled={refreshingFinancials}
                  title="Download the latest accounts from Companies House and re-score"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: 34,
                    boxSizing: "border-box",
                    background: "var(--surface)",
                    border: "1px solid var(--border-strong)",
                    borderRadius: "var(--radius-sm)",
                    padding: "0 12px",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "var(--sans)",
                    color: "var(--accent)",
                    cursor: refreshingFinancials ? "wait" : "pointer",
                    opacity: refreshingFinancials ? 0.6 : 1,
                    transition: "var(--transition)",
                  }}
                >
                  {refreshingFinancials
                    ? "Fetching from Companies House…"
                    : "Fetch latest accounts"}
                </button>
              )}
              {filedAccountsUrl && (
                <a
                  href={filedAccountsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    height: 24,
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--accent)",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  View filed accounts (PDF)
                  <span aria-hidden="true">↗</span>
                </a>
              )}
            </div>
          )}
          {refreshFinancialsError && (
            <div style={{ fontSize: 11, color: "var(--red)" }}>
              {refreshFinancialsError}
            </div>
          )}
        </div>
      )}

      {/* Meta */}
      {meta && (
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            padding: "8px 12px",
            background: "var(--surface-raised)",
            borderRadius: 6,
            border: "1px solid var(--border)",
          }}
        >
          {[
            meta.sic && { label: "SIC", value: meta.sic },
            meta.ageDays != null && { label: "Age", value: `${meta.ageDays}d` },
            { label: "Website", value: meta.hasWebsite ? "✓" : "✗" },
            { label: "Phone", value: meta.hasPhone ? "✓" : "✗" },
          ]
            .filter(Boolean)
            .map((item) => (
              <div
                key={item.label}
                style={{ display: "flex", gap: 4, alignItems: "center" }}
              >
                <span
                  style={{
                    fontSize: 10,
                    color: "var(--text-faint)",
                    fontFamily: "var(--mono)",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-muted)",
                    fontFamily: "var(--mono)",
                  }}
                >
                  {item.value}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IDLE STATE, shown before the user triggers scoring
// ─────────────────────────────────────────────────────────────────────────────

function IdlePrompt({ onScore, websiteAvailable }) {
  // Always allow scoring, all four products can be evaluated without a website.
  // Show the website note only when a website exists.
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: "28px 16px",
        background: "var(--surface-raised)",
        border: "1px dashed rgba(0,0,0,0.12)",
        borderRadius: 8,
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: "rgba(8,42,88,0.06)",
          border: "1px solid rgba(8,42,88,0.14)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--accent)",
        }}
      >
        <svg
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      </div>
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text)",
            marginBottom: 4,
          }}
        >
          Opportunity score not yet generated
        </div>
        <div
          style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}
        >
          Run scoring to see product fit and recommended actions for this
          company.
        </div>
        {websiteAvailable !== false && (
          <div
            style={{
              fontSize: 11,
              color: "var(--text-faint)",
              marginTop: 4,
              fontStyle: "italic",
            }}
          >
            AXO will take a fresh snapshot of the website.
          </div>
        )}
      </div>
      <button
        onClick={() => onScore(true)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 16px",
          borderRadius: 6,
          background: "var(--accent)",
          color: "#fff",
          fontSize: 12,
          fontWeight: 600,
          border: "none",
          cursor: "pointer",
          letterSpacing: "0.01em",
          transition: "opacity 0.15s",
        }}
      >
        Run Opportunity Score
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const OpportunityScore = ({
  companyId,
  companyNumber = null,
  canRefreshFinancials = true,
  compact = false,
  className = "",
  cachedScore = null,
  websiteAvailable,
  onScoreLoaded = null,
  serviceProfiles = [],
  // isInternal: true  → use the live apiService (full Peach product scores)
  //             false → use demoApiService (demo-safe scores, no internal brands)
  isInternal = true,
}) => {
  // "idle" = not yet triggered | "loading" | "done" | "error"
  const [status, setStatus] = useState("idle");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshingFinancials, setRefreshingFinancials] = useState(false);
  const [refreshFinancialsError, setRefreshFinancialsError] = useState(null);

  // Pick the right API backend based on the caller's access level.
  const api = isInternal ? apiService : demoApiService;

  const load = useCallback(
    async (forceRefresh = false) => {
      if (!companyId) return;
      setStatus("loading");
      setError(null);
      try {
        let result;
        if (forceRefresh && websiteAvailable) {
          try {
            // Refresh the AXO website snapshot, then re-score. This only works
            // when the enrichment row has a website, so fall through to a
            // normal score if it doesn't (e.g. the website comes from credit
            // data or a manual override). Scoring never requires a website.
            result = await api.refreshWebsiteHealth(companyId);
          } catch (refreshErr) {
            result = await api.getCompanyScore(companyId);
          }
        } else {
          // Normal GET – uses cache or returns immediately without waiting
          result = await api.getCompanyScore(companyId);
        }
        setData(result.data);
        setStatus("done");
        onScoreLoaded?.(result.data);
      } catch (err) {
        setError("Unable to load score");
        setStatus("error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, onScoreLoaded, websiteAvailable, api],
  );

  // Download the latest filed accounts (Companies House Document API) and
  // re-score, so the Financial health panel reflects the new balance sheet.
  const refreshFinancials = useCallback(async () => {
    if (!companyNumber || !isInternal) return;
    setRefreshingFinancials(true);
    setRefreshFinancialsError(null);
    try {
      await api.refreshCompanyFinancials(companyNumber);
      await load();
    } catch (err) {
      setRefreshFinancialsError(
        err?.response?.data?.message ||
          "Could not fetch the latest filed accounts",
      );
    } finally {
      setRefreshingFinancials(false);
    }
  }, [companyNumber, isInternal, api, load]);

  // Compact rows auto-fetch on mount / companyId change (no force refresh)
  useEffect(() => {
    if (!compact) return;
    load();
  }, [compact, load]);

  // When expanding the drawer, restore cached score if available.
  // If no cache, show idle state.
  useEffect(() => {
    if (compact) return;
    if (cachedScore) {
      setData(cachedScore);
      setStatus("done");
    } else {
      setStatus("idle");
      setData(null);
      setError(null);
    }
  }, [compact, companyId, cachedScore]);

  // ── Compact pill view ────────────────────────────────────────────────────
  if (compact) {
    if (status === "loading" || status === "idle")
      return (
        <div style={{ display: "flex", gap: 4 }}>
          {[1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 64,
                height: 20,
                borderRadius: 9999,
                background:
                  "linear-gradient(90deg, var(--border) 25%, var(--surface-sunken) 50%, var(--border) 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.4s infinite",
              }}
            />
          ))}
        </div>
      );
    if (status === "error" || !data)
      return (
        <span style={{ color: "var(--text-faint)", fontSize: 12 }}>-</span>
      );
    return <CompactScore data={data} />;
  }

  // ── Full panel ────────────────────────────────────────────────────────────
  const loading = status === "loading";

  return (
    <div className={className} style={{ fontFamily: "var(--sans)" }}>
      {/* Header row, always visible */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "var(--text-faint)",
            fontFamily: "var(--mono)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <svg
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          Opportunity Score
        </div>

        {/* Refresh button, only shown once a score has been loaded */}
        {status === "done" && (
          <button
            onClick={() => load(true)}
            disabled={loading}
            title="Refresh score"
            style={{
              background: "none",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              color: "var(--text-faint)",
              padding: 4,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              opacity: loading ? 0.4 : 1,
              transition: "opacity 0.15s",
            }}
          >
            <svg
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              style={{
                animation: loading ? "spin 0.8s linear infinite" : "none",
              }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        )}
      </div>

      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>

      {/* ── Idle: show CTA ── */}
      {status === "idle" && (
        <IdlePrompt onScore={load} websiteAvailable={websiteAvailable} />
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                height: 66,
                borderRadius: 8,
                background:
                  "linear-gradient(90deg, var(--border) 25%, var(--surface-sunken) 50%, var(--border) 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.4s infinite",
              }}
            />
          ))}
        </div>
      )}

      {/* ── Error ── */}
      {status === "error" && (
        <div
          style={{
            padding: "12px 14px",
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            borderRadius: 8,
            fontSize: 12,
            color: "var(--red)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <svg
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {error}
          <button
            onClick={load}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "1px solid rgba(220,38,38,0.3)",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 11,
              color: "var(--red)",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Results ── */}
      {status === "done" && data && (
        <FullScorePanel
          data={data}
          serviceProfiles={serviceProfiles}
          onRefreshFinancials={
            isInternal && companyNumber && canRefreshFinancials
              ? refreshFinancials
              : null
          }
          refreshingFinancials={refreshingFinancials}
          refreshFinancialsError={refreshFinancialsError}
        />
      )}
    </div>
  );
};

export default OpportunityScore;
